import { Spacer, Flex, Spinner } from "@hackthenorth/north";
import { Label, Checkbox, Link } from "north.js";
import React from "react";
// import { OptionsType, OptionTypeBase } from "react-select";
import { default as ReactSelect } from "react-select";

import { useGetFavoritedHackersQuery } from "src/api/sponsors/getFavoritedHackers.generated";
import { Route } from "src/shared/constants/route";
// import { formatTimeV2 } from "src/shared/utils/formatTime";
import {
  // answersToOptions,
  formatSelectedOptions,
  optionsToAnswers,
  optionsToNorthV2Options,
  // timeToOptionV2,
} from "src/shared/utils/react-select";
// import { CoffeeChatsData } from "src/views/sponsor/perks/sync";
import {
  PerkStatus,
  SponsorPerksData,
  SponsorPerkType,
} from "src/views/sponsor/perks/types";
import { getApplicationStages } from "src/views/sponsor/recruitment/utils";

import { PerkContainer, PerkRepresentatives, PerkSelect } from "../components";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription, TextInputTitle } from "../TextComponents";
import { PerkData } from "../usePerkState";

import { DURATION_OPTIONS } from "./constants";
import { formatCoffeeChatStartTime } from "./utils";

export const COFFEE_CHATS_VALIDATION_FIELDS: (keyof SponsorPerksData)[] = [
  "coffee_chats_duration",
];

export const CoffeeChats: React.FC<PerkData> = ({
  getState,
  updateState,
  updateStates,
  isReadOnly,
}) => {
  const names = getState("coffee_chats_representatives") ?? [];
  const emails = getState("coffee_chats_representatives_emails") ?? [];
  const pronouns = getState("coffee_chats_representatives_pronouns") ?? [];

  const resetRows = () =>
    updateStates([
      ["coffee_chats_representatives", Array(5).fill("")],
      ["coffee_chats_representatives_emails", Array(5).fill("")],
      ["coffee_chats_representatives_pronouns", Array(5).fill("")],
    ]);

  const addRow = () => {
    updateStates([
      ["coffee_chats_representatives", [...names, ""]],
      ["coffee_chats_representatives_emails", [...emails, ""]],
      ["coffee_chats_representatives_pronouns", [...pronouns, ""]],
    ]);
  };

  const deleteRow = (i: number) => {
    updateStates([
      [
        "coffee_chats_representatives",
        [...names.slice(0, i), ...names.slice(i + 1)],
      ],
      [
        "coffee_chats_representatives_emails",
        [...emails.slice(0, i), ...emails.slice(i + 1)],
      ],
      [
        "coffee_chats_representatives_pronouns",
        [...pronouns.slice(0, i), ...pronouns.slice(i + 1)],
      ],
    ]);
  };

  const updateNames = (val: string[]) =>
    updateState("coffee_chats_representatives", val);

  const updateEmails = (val: string[]) =>
    updateState("coffee_chats_representatives_emails", val);

  const updatePronouns = (val: string[]) =>
    updateState("coffee_chats_representatives_pronouns", val);

  const favoritedHackerIds =
    getState("coffee_chats_favorited_hackers")?.map((id: string) =>
      parseInt(id)
    ) ?? [];
  const { data, loading: fetchingHackers } = useGetFavoritedHackersQuery({
    variables: {
      stages: getApplicationStages("all"),
      favoritedHackerIds,
    },
    skip: favoritedHackerIds.length === 0,
  });

  const hackerOptions =
    data?.getSponsorRecruitmentCandidates.candidates.map((hacker) => ({
      label: hacker.name,
      value: hacker.id.toString(),
    })) ?? [];

  const selectedHackers = hackerOptions.filter((option) =>
    getState("coffee_chats_hackers")?.includes(option.value)
  );

  return (
    <>
      <Header
        title="Coffee Chats"
        subtitle="This perk is to schedule coffee chats with hackers."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("coffee_chats_status") as PerkStatus | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("coffee_chats_status") as PerkStatus | undefined
            )}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.COFFEE_CHATS}
        readOnly={isReadOnly}
      >
        <TextInputTitle style={{ fontSize: "20px" }}>
          Your Assigned Coffee Chat Time
        </TextInputTitle>
        <Spacer height={8} />
        <TextDescription>
          {formatCoffeeChatStartTime(
            getState("coffee_chats_start_time"),
            getState("coffee_chats_end_date")
          )}
        </TextDescription>
        <Spacer height={8} />
        <Spacer height={24} />
        <TextInputTitle style={{ fontSize: "20px" }}>Duration</TextInputTitle>
        <Spacer height={8} />
        <TextDescription>How long will each Coffee Chat be? </TextDescription>
        <Spacer height={8} />
        <PerkSelect
          fullWidth
          size="lg"
          onChange={(e) => updateState("coffee_chats_duration", e.target.value)}
          value={getState("coffee_chats_duration") ?? ""}
          isReadOnly={isReadOnly}
        >
          {optionsToNorthV2Options(
            DURATION_OPTIONS as { label: string; value: string }[]
          )}
        </PerkSelect>
        <Spacer height={24} />
        <PerkRepresentatives
          names={names}
          emails={emails}
          pronouns={pronouns}
          isReadOnly={isReadOnly}
          description={
            <TextDescription>
              Which representatives will be participating in Coffee Chats?
            </TextDescription>
          }
          updateNames={updateNames}
          updateEmails={updateEmails}
          updatePronouns={updatePronouns}
          resetRows={resetRows}
          addRow={addRow}
          deleteRow={deleteRow}
        />
        <Spacer height={24} />
        <TextInputTitle style={{ fontSize: "20px" }}>Hackers</TextInputTitle>
        <Spacer height={8} />
        <TextDescription>
          Which hackers would you like to talk with? Note: This list only shows
          starred hackers who have indicated interest in having a Coffee Chat
          with your company. To star a hacker, head to the{" "}
          <Link href={Route.SPONSOR_RECRUITMENT}>Recruitment page</Link>
        </TextDescription>
        <Spacer height={16} />
        {fetchingHackers ? (
          <Spinner />
        ) : (
          <ReactSelect
            options={hackerOptions}
            isMulti
            isDisabled={isReadOnly}
            onChange={(options) =>
              updateState("coffee_chats_hackers", optionsToAnswers(options))
            }
            value={selectedHackers}
            placeholder="Select favorited hackers"
          />
        )}
        {selectedHackers.length > 0 && (
          <>
            <Spacer height={4} />
            <TextDescription>
              {formatSelectedOptions(selectedHackers)}
            </TextDescription>
          </>
        )}
        <Spacer height={16} />
        <Flex>
          <Checkbox
            checked={
              selectedHackers.length === hackerOptions.length &&
              hackerOptions.length !== 0
            }
            disabled={isReadOnly}
            onChange={(e) =>
              updateState(
                "coffee_chats_hackers",
                e.target.value ? optionsToAnswers(hackerOptions) : []
              )
            }
            label="Select all starred hackers"
          />
        </Flex>
      </PerkContainer>
    </>
  );
};

const Description = () => (
  <>
    <TextDescription>
      Coffee Chats are 1:1 scheduled chats between representatives from your
      company and selected hackers who have indicated interest in connecting
      with your team.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      To select hackers for Coffee Chats, favorite the hackers you’d like to
      chat with in the Recruitment section of the dashboard. You can also sort
      by “Interested in Company” to see hackers who have specifically indicated
      interest in your company.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      Once you’ve made your selections, we’ll email those hackers with a link to
      RSVP for a Coffee Chat with your team. We’ll then assign confirmed hackers
      to your representatives based on your preferred time slots and share the
      final chat order with you before the event.
    </TextDescription>
    <Spacer height={16} />
    <TextDescription>
      We recommend keeping Coffee Chats to no more than 2 hours so all scheduled
      chats can be completed during your assigned time slot.
    </TextDescription>
  </>
);
